import {FC, useEffect, useMemo, useState} from 'react';
import {Box, Checkbox, FormControlLabel, Grid, SelectChangeEvent, Typography,} from '@mui/material';
import {Form, Formik, FormikConfig, FormikHandlers, FormikProps} from 'formik';
import {FormattedMessage, useIntl} from 'react-intl';
import {getHelperText} from '../../../utils/forms';
import {AMPInput} from '../../common/AMPInput';
import {signUpCompanyInitialValues, SignUpCorporateType} from '../formData/common/initialData';
import {signUpCorporateSchema} from '../formData/signUp';
import {registerApi} from 'src/api';
import {actionsWrapperSx, formSx, requiredFieldSx} from './SignUpFormForCorporateUser.sx';
import {AppDispatch} from 'src/app/store';
import {NavigateFunction, useNavigate} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from 'src/app/hooks';
import {SubmitButton} from 'src/components/common/SubmitButton';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {AMPFormSelect} from 'src/components/common/AMPFormSelect';
import {AppRouteEnum, AppRouteSpace, isErrorObject, valuesTrimmer} from 'src/utils/helper';
import {TermsPrivacyAgreementLabel} from 'src/components/common/TermsPrivacyAgreementLabel';
import {NotifyError} from 'src/utils/notification';
import {systemApi} from '../../../api/system';
import {CompanyRegistrationRequest, SelectValue} from '../../../types';
import {handleGetCountries} from '../../../hooks/useGetCountries';
import {TariffType} from '../formData/tariff';
import {allRegionsValue} from '../../../utils/consts';
import {AMPPhoneNumber} from 'src/components/common/AMPPhoneNumber';

type SignUpMutationTrigger = ReturnType<typeof registerApi.useRegisterCompaniesMutation>[0];

const handleSubmit =
    (
        trigger: SignUpMutationTrigger,
        dispatch: AppDispatch,
        navigate: NavigateFunction
    ): FormikConfig<SignUpCorporateType>['onSubmit'] =>
        async (values) => {
            try {
                const value = valuesTrimmer(values);
                dispatch(preloaderChangeStatus(true));
                const {
                    city,
                    country,
                    region,
                    street,
                    zipCode,
                    houseNumber,
                    firstName,
                    lastName,
                    companyName,
                    email,
                    phoneNumber,
                    stationsQuantity,
                } = value;

                const data: CompanyRegistrationRequest = {
                    businessId: 1, // TODO: replace on business id parameter
                    companyName,
                    email,
                    firstName,
                    lastName,
                    phoneNumber,
                    stationsQuantity,
                    parkingAddress: {
                        city,
                        country,
                        region,
                        street,
                        zipCode,
                        houseNumber,
                    },
                };

                await trigger(data).unwrap();

                navigate(AppRouteSpace([AppRouteEnum.corporateUserWelcome]));
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                dispatch(preloaderChangeStatus(false));
            }
        };

export const SignUpFormForCorporateUser: FC = () => {
    const intl = useIntl();
    const {lang} = useAppSelector((state) => state.common);
    const [trigger] = registerApi.useRegisterCompaniesMutation();

    const navigate = useNavigate();
    const [getCountries, countries] = systemApi.useLazyGetCountriesQuery();
    const dispatch = useAppDispatch();
    const [activeCountry, setActiveCountry] = useState<string>('');

    const processedCountries =
        countries?.data?.map((country) => ({
            id: country.id,
            name: intl.formatMessage({id: country.name.toLowerCase()}),
            value: country.name,
        })) ?? [];

    const processedRegions = useMemo(() => {
        const initialArray: SelectValue[] = [
            {
                id: 0,
                name: activeCountry.length ? intl.formatMessage({id: activeCountry.toLowerCase()}) : '',
                value: allRegionsValue,
            },
        ];
        if (activeCountry.length) {
            const mappedRegions: SelectValue[] | undefined = countries.data
                ?.find((country) => country.name === activeCountry)
                ?.regions?.map((region) => ({
                    id: region.id,
                    name: intl.formatMessage({id: region.name}),
                    value: region.name,
                }));
            return mappedRegions?.concat(initialArray[0]) ?? initialArray;
        }
        return initialArray;
    }, [activeCountry, lang]);

    const handleChangeCountry =
        (
            handleChange: FormikHandlers['handleChange'],
            setFieldValue: FormikProps<TariffType>['setFieldValue']
        ) =>
            (event: SelectChangeEvent) => {
                setActiveCountry(event.target.value);
                setFieldValue('region', '');
                return handleChange(event);
            };

    const handleChangePhone =
        (setFieldValue: FormikProps<TariffType>['setFieldValue'], fieldName: string) =>
            (newValue: string | number | null | undefined) => {
                if (newValue) {
                    setFieldValue(fieldName, `+${newValue}`);
                }
            };

    useEffect(() => {
        void handleGetCountries(getCountries, dispatch);
    }, []);

    return (
        <Formik
            initialValues={signUpCompanyInitialValues}
            validationSchema={signUpCorporateSchema}
            onSubmit={handleSubmit(trigger, dispatch, navigate)}
        >
            {({
                  errors,
                  handleBlur,
                  handleChange,
                  setFieldValue,
                  handleSubmit,
                  isSubmitting,
                  touched,
                  values,
              }: FormikProps<SignUpCorporateType>) => (
                <Form
                    onSubmit={handleSubmit}
                    noValidate
                >
                    <Box sx={formSx}>
                        <Typography
                            variant="caption"
                            color="textPrimary"
                            sx={requiredFieldSx}
                        >
                            <span>*</span>
                            {intl.formatMessage({id: 'requiredFields'})}
                        </Typography>
                    </Box>

                    {/* Company name */}
                    <AMPInput
                        value={values.companyName}
                        type={'companyName'}
                        label={'companyName'}
                        input={'companyName'}
                        onChange={handleChange}
                        required
                        onBlur={handleBlur}
                        hasError={touched.companyName && Boolean(errors.companyName)}
                        helperText={getHelperText('companyName', touched, errors, intl)}
                    />

                    <Box sx={formSx}>
                        <Typography
                            variant="subtitle1"
                            color="textPrimary"
                        >
                            <FormattedMessage id="contractPerson"/>
                        </Typography>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* First name */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.firstName}
                                    type={'firstName'}
                                    label={'firstName'}
                                    input={'firstName'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.firstName && Boolean(errors.firstName)}
                                    helperText={getHelperText('firstName', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Last name */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.lastName}
                                    type={'lastName'}
                                    label={'lastName'}
                                    input={'lastName'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.lastName && Boolean(errors.lastName)}
                                    helperText={getHelperText('lastName', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* Email address */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.email}
                                    type={'emailAddress'}
                                    label={'emailAddress'}
                                    input={'email'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.email && Boolean(errors.email)}
                                    helperText={getHelperText('email', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Phone number */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPPhoneNumber
                                    value={values.phoneNumber}
                                    onChange={handleChangePhone(setFieldValue, 'phoneNumber')}
                                    label={'phoneNumber'}
                                    onBlur={handleBlur}
                                    hasError={touched.phoneNumber && Boolean(errors.phoneNumber)}
                                    helperText={getHelperText('phoneNumber', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={formSx}>
                        <Typography
                            variant="subtitle1"
                            color="textPrimary"
                        >
                            <FormattedMessage id="parkingAddress"/>
                        </Typography>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* Country */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPFormSelect
                                    data={processedCountries}
                                    value={values.country}
                                    label={'country'}
                                    input={'country'}
                                    required
                                    onChange={handleChangeCountry(handleChange, setFieldValue)}
                                    onBlur={handleBlur}
                                    hasError={touched.country && Boolean(errors.country)}
                                    helperText={getHelperText('country', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Region */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPFormSelect
                                    data={processedRegions}
                                    value={values.region as string}
                                    label={'region'}
                                    input={'region'}
                                    disabled={!activeCountry.length}
                                    required
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    hasError={touched.region && Boolean(errors.region)}
                                    helperText={getHelperText('region', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* City */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    type="text"
                                    disabled={Boolean(!activeCountry)}
                                    value={values.city}
                                    label={'city'}
                                    input={'city'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.city && Boolean(errors.city)}
                                    helperText={getHelperText('city', touched, errors, intl)}
                                />
                            </Grid>

                            {/* Street */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.street}
                                    type={'street'}
                                    label={'street'}
                                    input={'street'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.street && Boolean(errors.street)}
                                    helperText={getHelperText('street', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* Building № */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.houseNumber}
                                    type={'houseNumber'}
                                    label={'houseNumber'}
                                    input={'houseNumber'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.houseNumber && Boolean(errors.houseNumber)}
                                    helperText={getHelperText('houseNumber', touched, errors, intl)}
                                />
                            </Grid>

                            {/* ZIP code */}
                            <Grid
                                md={3}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.zipCode}
                                    type={'zipCode'}
                                    label={'zipCode'}
                                    input={'zipCode'}
                                    onChange={handleChange}
                                    required
                                    onBlur={handleBlur}
                                    hasError={touched.zipCode && Boolean(errors.zipCode)}
                                    helperText={getHelperText('zipCode', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>

                        <Grid
                            container
                            spacing={2}
                        >
                            {/* Number of parking lot */}
                            <Grid
                                md={6}
                                xs={12}
                                item
                            >
                                <AMPInput
                                    value={values.stationsQuantity}
                                    type={'text'}
                                    label={'stationsQuantityRequired'}
                                    input={'stationsQuantity'}
                                    onChange={handleChange}
                                    hasError={touched.stationsQuantity && Boolean(errors.stationsQuantity)}
                                    helperText={getHelperText('stationsQuantity', touched, errors, intl)}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Box sx={actionsWrapperSx}>
                        <FormControlLabel
                            label={<TermsPrivacyAgreementLabel/>}
                            control={
                                <Checkbox
                                    color="secondary"
                                    name="agreements"
                                    value={values.agreements}
                                    onChange={handleChange}
                                    required
                                />
                            }
                        />
                        <SubmitButton
                            text="signUp"
                            isSubmitting={isSubmitting || !values.agreements}
                            rounding
                            color="brand"
                        />
                    </Box>
                </Form>
            )}
        </Formik>
    );
};
